import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { login } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';

export function LoginScreen() {
  const nav = useNavigate();
  const setSession = useAuthStore(s => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { token, user } = await login({ email: email.trim(), password });
      if (user.role !== 'shareholder' && user.role !== 'admin') {
        setError('This portal is for shareholders only.');
        setLoading(false);
        return;
      }
      setSession(token, {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role
      });
      nav('/', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Sign-in failed. Check your email and password.';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-nurev-bg px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700">
            <TrendingUp className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="text-base font-bold leading-tight text-nurev-text">
              NUREV
            </p>
            <p className="text-xs leading-tight text-nurev-muted">
              Investor Portal
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-nurev-border bg-nurev-surface p-7">
          <h1 className="mb-1 text-xl font-bold text-nurev-text">Sign in</h1>
          <p className="mb-6 text-sm text-nurev-muted">
            Use the email + password you set when you accepted your shareholder
            invite.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text focus:border-emerald-500/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text focus:border-emerald-500/50 focus:outline-none"
              />
            </div>

            {error && <p className="text-xs text-red-300">{error}</p>}

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full rounded-lg bg-emerald-500 py-2.5 text-sm font-medium text-nurev-bg transition hover:bg-emerald-400 disabled:opacity-40"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-5 text-center text-[10px] text-nurev-muted">
            Just got invited?{' '}
            <Link to="/accept-invite" className="text-emerald-300 hover:text-emerald-200">
              Accept your invite
            </Link>
            .
          </p>
        </div>

        <p className="mt-6 text-center text-[10px] text-nurev-muted">
          Trouble signing in? Email{' '}
          <a
            href="mailto:contact@nurev.io"
            className="text-emerald-300 hover:text-emerald-200"
          >
            contact@nurev.io
          </a>
        </p>
      </div>
    </div>
  );
}
